import axios from "axios";
import { useState } from "react";
import BackgroundVisuals from "../components/BackgroundVisuals";

export const backendUrl = "https://zuris-backend.vercel.app"; 
// export const backendUrl = "http://localhost:8000"; 

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState({
    fullname: "",
    email: "",
    company: "",
    service: "",
    project_details: "",
    phoneNumber: "" 
  });

  const validateField = (name, value) => {
    if (!value || value.trim() === '') {
      if (name === 'fullname' || name === 'email' || name === 'company' || name === 'project_details') {
        return ''; 
      }
      return ''; 
    }

    switch(name) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Please enter a valid email address';
      case 'phoneNumber':
        return !/^\+?[\d\s-]{10,}$/.test(value) ? 'Please enter a valid phone number' : '';
      case 'fullname':
        return value.trim().length < 2 ? 'Name must be at least 2 characters' : '';
      case 'company':
        return value.trim().length < 2 ? 'Company name is required' : '';
      case 'project_details':
        return value.trim().length < 10 ? 'Please provide more details (min 10 characters)' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({...form, [name]: value});
    if (successMessage) setSuccessMessage('');
    const error = validateField(name, value);
    setErrors({...errors, [name]: error});
  };

  const validateAllFields = () => {
    const newErrors = {};
    Object.keys(form).forEach(key => {
      if (key !== 'service') {
        const error = validateField(key, form[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submithandler = async (e) => {
    e.preventDefault();
    if (!validateAllFields()) return;
    setLoading(true);
    try {
      const { fullname, email, company, service, project_details, phoneNumber } = form;
      const response = await axios.post(backendUrl + '/api/form/submit', 
        { fullname, email, company, service, project_details, phoneNumber },
        { timeout: 10000 } 
      );
      if (response.data.success) {
        setSuccessMessage(" Message sent successfully! We'll contact you within 48 hours.");
        setForm({ fullname: '', email: '', company: '', service: '', project_details: '', phoneNumber: '' });
        setErrors({});
      } else {
        setErrors({ submit: "Failed to send message. Please try again." });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      let errorMessage = "An unexpected error occurred. Please try again.";
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. Please check your connection.";
      } else if (error.response) {
        errorMessage = `Server error: ${error.response.status}.`;
      } else if (error.request) {
        errorMessage = "Network error. Please check your internet connection.";
      }
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pt-20 min-h-screen bg-white py-20 relative selection:bg-[#952877]/10 selection:text-[#952877]">
      <BackgroundVisuals />
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <div className="text-center mt-12 mb-12">
          <h1 className="text-5xl font-black mb-8">
            <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent ">
              Contact Us
            </span>
          </h1>
          <p className="text-xl text-gray-500 font-light">Ready to transform your business with AI? Let's start the conversation.</p>
        </div>
        
        <div className="bg-white/40 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)] border border-white/60 p-8 md:p-12">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-6 mb-8 rounded-xl" role="alert">
              <p className="font-bold uppercase tracking-wider text-sm">{successMessage}</p>
            </div>
          )}

          {/* Submit Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-6 mb-8 rounded-xl" role="alert">
              <p className="font-bold uppercase tracking-wider text-sm">{errors.submit}</p>
            </div>
          )}

          <form className="space-y-8" onSubmit={submithandler} noValidate>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="fullname" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                  Full Name <span className="text-[#952877]">*</span>
                </label>
                <input 
                  id="fullname"
                  name="fullname"
                  value={form.fullname} 
                  onChange={handleChange}
                  type="text" 
                  className={`w-full p-5 bg-white/60 border rounded-2xl focus:ring-4 focus:ring-[#952877]/5 outline-none transition-all font-bold text-[#17091a] ${
                    errors.fullname ? 'border-red-400' : 'border-gray-100 focus:border-[#952877]/20'
                  }`}
                  placeholder="Your Name" 
                />
                {errors.fullname && (
                  <p className="text-red-500 text-[10px] font-bold uppercase mt-2 ml-1 tracking-wider">{errors.fullname}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                  Email Address <span className="text-[#952877]">*</span>
                </label>
                <input 
                  id="email"
                  name="email"
                  value={form.email} 
                  onChange={handleChange}
                  type="email" 
                  className={`w-full p-5 bg-white/60 border rounded-2xl focus:ring-4 focus:ring-[#952877]/5 outline-none transition-all font-bold text-[#17091a] ${
                    errors.email ? 'border-red-400' : 'border-gray-100 focus:border-[#952877]/20'
                  }`}
                  placeholder="your@email.com" 
                />
                {errors.email && (
                  <p className="text-red-500 text-[10px] font-bold uppercase mt-2 ml-1 tracking-wider">{errors.email}</p>
                )}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="company" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                  Company <span className="text-[#952877]">*</span>
                </label>
                <input 
                  id="company"
                  name="company"
                  value={form.company} 
                  onChange={handleChange}
                  type="text" 
                  className={`w-full p-5 bg-white/60 border rounded-2xl focus:ring-4 focus:ring-[#952877]/5 outline-none transition-all font-bold text-[#17091a] ${
                    errors.company ? 'border-red-400' : 'border-gray-100 focus:border-[#952877]/20'
                  }`}
                  placeholder="Your Company" 
                />
                {errors.company && (
                  <p className="text-red-500 text-[10px] font-bold uppercase mt-2 ml-1 tracking-wider">{errors.company}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phoneNumber" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                  Phone Number
                </label>
                <input 
                  id="phoneNumber"
                  name="phoneNumber"
                  value={form.phoneNumber} 
                  onChange={handleChange}
                  type="tel" 
                  className={`w-full p-5 bg-white/60 border rounded-2xl focus:ring-4 focus:ring-[#952877]/5 outline-none transition-all font-bold text-[#17091a] ${
                    errors.phoneNumber ? 'border-red-400' : 'border-gray-100 focus:border-[#952877]/20'
                  }`}
                  placeholder="+91 xxxxx xxxxx" 
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-[10px] font-bold uppercase mt-2 ml-1 tracking-wider">{errors.phoneNumber}</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="service" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                Service Interest
              </label>
              <select 
                id="service"
                name="service"
                value={form.service} 
                onChange={handleChange}
                className="w-full p-5 bg-white/60 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-[#952877]/5 focus:border-[#952877]/20 outline-none transition-all font-bold text-[#17091a] appearance-none cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="AI Consultation & Advisory">AI Consultation & Advisory</option>
                <option value="AI Automation & Transformation">AI Automation & Transformation</option>
                <option value="Data Intelligence & Analytics Platform">Data Intelligence & Analytics Platform</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="project_details" className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#17091a] mb-3 ml-1">
                Project Details <span className="text-[#952877]">*</span>
              </label>
              <textarea 
                id="project_details"
                name="project_details"
                value={form.project_details} 
                onChange={handleChange}
                rows="6" 
                className={`w-full p-6 bg-white/60 border rounded-[2rem] focus:ring-4 focus:ring-[#952877]/5 outline-none transition-all font-medium text-[#17091a] leading-relaxed resize-none ${
                  errors.project_details ? 'border-red-400' : 'border-gray-100 focus:border-[#952877]/20'
                }`}
                placeholder="Tell us about your AI project..."
              />
              {errors.project_details && (
                <p className="text-red-500 text-[10px] font-bold uppercase mt-2 ml-1 tracking-wider">{errors.project_details}</p>
              )}
            </div>
            
            <button 
              disabled={loading} 
              type="submit" 
              className="w-full bg-[#17091a] text-white py-6 rounded-2xl font-black text-xs uppercase tracking-[0.4em] relative overflow-hidden group transition-all duration-500 hover:shadow-[0_20px_40px_rgba(23,9,26,0.3)] disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
              <span className="relative z-10">
                {loading ? "Transmitting..." : "Submit & Book Consultation"}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Contact;